/**
 * Busca em memória pelas listas de cadastro: sem acento e sem diferença
 * de maiúscula, como o `upper(unaccent())` que o servidor usa nas
 * buscas dele (`/channels`, `/vehicles`) — quem digita "saude" tem que
 * achar "Saúde" nas duas.
 *
 * Mora aqui porque a mesma comparação já estava escrita dentro da tela
 * de CENA, e as abas de Ajustes iam ser a segunda, a terceira… a décima
 * cópia (2026-10-03).
 */

/** Caixa baixa e sem acento. */
export function foldText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase("pt-BR");
}

/**
 * As letras que podem levar acento, com todas as variantes nas duas
 * caixas. Gêmea de `_ACCENT_VARIANTS` em `core/mention_search.py`: a
 * busca de menções e de justificativas casa no servidor com estas
 * classes, e o destaque daqui tem que marcar o mesmo.
 */
export const ACCENT_VARIANTS: Readonly<Record<string, string>> = {
  a: "aáàâãäåAÁÀÂÃÄÅ",
  e: "eéèêëEÉÈÊË",
  i: "iíìîïIÍÌÎÏ",
  o: "oóòôõöOÓÒÔÕÖ",
  u: "uúùûüUÚÙÛÜ",
  c: "cçCÇ",
  n: "nñNÑ",
  y: "yýÿYÝŸ",
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * O texto digitado como expressão regular (fonte, sem flags) que casa
 * sem acento — use com a flag `i` pra também casar sem caixa. Cada letra
 * que pode levar acento vira a classe das variantes, o resto vai
 * escapado, e espaço interno vira `\s+`, como no servidor.
 */
export function accentInsensitivePattern(text: string): string {
  return text
    .normalize("NFC")
    .trim()
    .split(/\s+/)
    .map(piece =>
      Array.from(piece, char => {
        const variants = ACCENT_VARIANTS[foldText(char)];
        return variants ? `[${variants}]` : escapeRegExp(char);
      }).join("")
    )
    .join("\\s+");
}

/**
 * Os itens em que TODAS as palavras digitadas aparecem em algum dos
 * campos — "banco brasil" acha "Banco do Brasil", e a ordem das
 * palavras não importa. Busca vazia devolve a lista como veio (a mesma
 * referência: quem usa pode comparar pra saber se há filtro).
 *
 * `fields` devolve os textos de UM item; `null`/`undefined` no meio é
 * ignorado, pra quem chama não precisar filtrar campo opcional.
 */
export function filterBySearch<T>(
  items: T[],
  search: string,
  fields: (item: T) => Array<string | null | undefined>
): T[] {
  const words = foldText(search).split(/\s+/).filter(Boolean);
  if (words.length === 0) return items;
  return items.filter(item => {
    const haystack = foldText(
      fields(item)
        .filter((field): field is string => typeof field === "string")
        .join(" \u0000 ")
    );
    return words.every(word => haystack.includes(word));
  });
}
